// src/components/3D/TownshipPlanner3D.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Sky, Environment } from '@react-three/drei';
import BuildingMesh from './BuildingMesh';
import GardenMesh from './GardenMesh';
import Ground from './Ground';

const TownshipPlanner3D = ({ elements, plotSize }) => {
  const [cameraPosition, setCameraPosition] = useState([30, 30, 30]);
  const [groundDimensions, setGroundDimensions] = useState({
    width: plotSize.width,
    height: plotSize.height
  });

  const calculateGroundDimensions = useCallback(() => {
    const padding = 600; // Increased padding
    let maxX = plotSize.width;
    let maxY = plotSize.height;
  
    elements.forEach(element => {
      const elementRight = element.x + element.width;
      const elementBottom = element.y + element.height;
      
      // If it's a water body, add extra margin
      const extraMargin = (element.type === 'Eco-Pond' || 
                          element.type === 'Community Lake' || 
                          element.properties.hasWaterFeature) ? element.width : 0;
      
      maxX = Math.max(maxX, elementRight + extraMargin);
      maxY = Math.max(maxY, elementBottom + extraMargin);
    });
  
    // Ensure minimum size and add padding
    return {
      width: Math.max(maxX + padding, plotSize.width),
      height: Math.max(maxY + padding, plotSize.height)
    };
  }, [elements, plotSize]);
  

  useEffect(() => {
    const newDimensions = calculateGroundDimensions();
    setGroundDimensions(newDimensions);

    const maxDimension = Math.max(newDimensions.width, newDimensions.height) / 40;
    setCameraPosition([maxDimension * 1.5, maxDimension, maxDimension * 1.5]);
  }, [elements, plotSize, calculateGroundDimensions]);

  const groundCenter = useMemo(() => [
    groundDimensions.width/80,
    0,
    groundDimensions.height/80
  ], [groundDimensions]);

  return (
    <div style={{ width: '100%', height: '600px' }}>
      <Canvas
        shadows
        camera={{ 
          position: cameraPosition,
          fov: 60,
          near: 0.1,
          far: 1000
        }}
      >
        <Sky sunPosition={[100, 100, 20]} />
        <Environment preset="city" />
        <ambientLight intensity={0.5} />
        <directionalLight
          position={[10, 10, 10]}
          intensity={1}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
        
        <Ground dimensions={groundDimensions} />
        
        {elements.map((element, index) => (
          element.category === 'building' ? (
            <BuildingMesh 
              key={element.id || index}
              element={element}
              position={[element.x/40, 0, element.y/40]}
            />
          ) : (
            <GardenMesh
              key={element.id || index}
              element={element}
              position={[element.x/40, 0, element.y/40]}
            />
          )
        ))}

        <OrbitControls 
          enableDamping
          dampingFactor={0.05}
          maxPolarAngle={Math.PI/2.1}
          target={groundCenter}
        />
      </Canvas>
    </div>
  );
};

export default TownshipPlanner3D;